<template>
    <div class="v-pagination">
        <el-button class="v-pagination-prev" :disabled="isFirstPage" size="small" icon="el-icon-arrow-left"
            @click="onPrev">上一页</el-button>
        <ul class="v-pager">
            <li class="v-pager-num" :class="{
                'v-pager-num-active': isFirstPage
            }" @click="currentPage = 1">1</li>
            <li class="v-pager-num" v-if="pagerState.showPrevMore" @click="onQuickPrev"><i class="el-icon-d-arrow-left" />
            </li>
            <li class="v-pager-num" v-for="pager in pagerState.pagers" :key="pager" :class="{
                'v-pager-num-active': currentPage === pager
            }" @click="currentPage = pager">{{ pager }}</li>
            <li class="v-pager-num" v-if="pagerState.showNextMore" @click="onQuickNext">
                <i class="el-icon-d-arrow-right" />
            </li>
            <li v-if="pageCount > 1" class="v-pager-num" :class="{
                'v-pager-num-active': isLastPage
            }" @click="currentPage = pageCount">{{ pageCount }}</li>
        </ul>
        <el-button class="v-pagination-prev" :disabled="isLastPage" size="small" @click="onNext">下一页<i
                class="el-icon-arrow-right" /></el-button>
        <div class="v-pagination-jump">
            前往<el-input class="v-pagination-editor" size="small" v-model="userInput" @change="onJumpChange" />页
        </div>
    </div>
</template>

<script>
import { computed, defineComponent, watch, ref, nextTick, toRef } from '@nuxtjs/composition-api';
import { useOffsetPagination } from '@vueuse/core';

export default defineComponent({
    name: 'VPagination',
    props: {
        total: Number,
        pageSize: Number,
        page: Number,
    },
    emits: ['current-change', 'update:page', 'size-change', 'prev-click', 'next-click'],
    setup(props, {
        emit
    }) {
        const pagerCount = 7;

        // const { page } = toRefs(props);
        const {
            currentPage,
            currentPageSize,
            pageCount,
            isFirstPage,
            isLastPage,
            prev,
            next
        } = useOffsetPagination({
            total: toRef(props, 'total'),
            page: props.page,
            pageSize: props.pageSize,
            onPageChange(page) {
                nextTick(() => {
                    if (props.page != currentPage.value) {
                        emit('update:page', page.currentPage)
                        emit('current-change', page.currentPage)
                    }
                })
            },
            onPageSizeChange({ currentPageSize }) {
                emit('size-change', currentPageSize)
            }
        })

        watch(() => props.page, (value) => {
            currentPage.value = value;
        })

        const onPrev = () => {
            prev();
            emit('prev-click');
        }

        const onNext = () => {
            console.log('next')
            next();
            nextTick(() => emit('next-click'))
        }

        const onQuickPrev = () => {
            const pagerCountOffset = pagerCount - 2;
            currentPage.value -= pagerCountOffset
        }

        const onQuickNext = () => {
            const pagerCountOffset = pagerCount - 2;
            currentPage.value += pagerCountOffset
        }

        // ======================= view pager state =======================
        const pagerState = computed(() => {

            const halfPagerCount = (pagerCount - 1) / 2;

            let showPrevMore = false;
            let showNextMore = false;

            if (pageCount.value > pagerCount) {
                if (currentPage.value > pagerCount - halfPagerCount) {
                    showPrevMore = true;
                }

                if (currentPage.value < pageCount.value - halfPagerCount) {
                    showNextMore = true;
                }
            }

            const array = []

            if (showPrevMore && !showNextMore) {
                const startPage = pageCount.value - (pagerCount - 2);
                for (let i = startPage; i < pageCount.value; i++) {
                    array.push(i);
                }
            } else if (!showPrevMore && showNextMore) {
                for (let i = 2; i < pagerCount; i++) {
                    array.push(i);
                }
            } else if (showPrevMore && showNextMore) {
                const offset = Math.floor(pagerCount / 2) - 1;
                for (let i = currentPage.value - offset; i <= currentPage.value + offset; i++) {
                    array.push(i);
                }
            } else {
                for (let i = 2; i < pageCount.value; i++) {
                    array.push(i);
                }
            }


            return {
                pagers: array,
                showPrevMore,
                showNextMore
            }
        })


        // ======================= jump =======================
        const userInput = ref(currentPage.value);
        watch(currentPage, (value) => {
            userInput.value = value
        }, {
            immediate: true
        })

        const onJumpChange = () => {
            nextTick(() => {
                currentPage.value = Number.isNaN(parseInt(userInput.value)) ? 1 : parseInt(userInput.value);
            })
        }

        return {
            pagerState,

            currentPage,
            currentPageSize,
            pageCount,
            isFirstPage,
            isLastPage,

            onPrev,
            onNext,

            onQuickPrev,
            onQuickNext,

            userInput,
            onJumpChange
        }
    }
})
</script>

<style lang="scss">
.v-pagination {
  display: -webkit-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  -webkit-align-items: center;
  align-items: center;
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
}
.v-pagination-prev, .v-pagination-next {
  padding-left: 1rem;
  padding-right: 1rem;
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
}
.v-pagination-prev:focus, .v-pagination-next:focus {
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
  background-color: #fff;
  border: 1px solid #dcdcdc;
}
.v-pagination-prev:hover, .v-pagination-next:hover {
  color: $theme-color;
  border-color: $theme-color;
  background-color: #e6effc;
}
.v-pagination-jump {
  margin-left: 1.5rem;
}
.v-pagination-editor {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  text-align: center;
  width: 3rem;
}
.v-pager {
  display: -webkit-box;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}
.v-pager-num {
  border-width: 1px;
  cursor: pointer;
  height: 2rem;
  line-height: 2rem;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  min-width: 2rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  text-align: center;
  --tw-text-opacity: 1;
  color: rgba(25, 25, 25, var(--tw-text-opacity));
  -webkit-transition-property: background-color, border-color, color, fill, stroke;
  -o-transition-property: background-color, border-color, color, fill, stroke;
  transition-property: background-color, border-color, color, fill, stroke;
  -webkit-transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  -o-transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-transition-duration: 150ms;
  -o-transition-duration: 150ms;
  transition-duration: 150ms;
}
.v-pager-num-active {
  font-weight: 500;
}
</style>